import { useCallback, useRef, useState } from "react";
import api from "@/lib/api";

export const MAX_SLIDE_BYTES = 8 * 1024 * 1024;

const newKey = () => `ck_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

export const validateSlideFile = (file) => {
  if (!file) return "No file";
  if (/\.hei[cf]$/i.test(file.name || "") || /hei[cf]/i.test(file.type || "")) {
    return "HEIC isn't supported. On iPhone, use a JPG or PNG.";
  }
  if (!/^image\//.test(file.type || "")) return "Only image files are supported";
  if (file.size > MAX_SLIDE_BYTES) return "Image must be 8 MB or smaller";
  return null;
};

export const describeUploadError = (e) => {
  const detail = e?.response?.data?.detail;
  const status = e?.response?.status;
  if (status === 400 && /format|jpg|jpeg|png|webp/i.test(String(detail || ""))) {
    return "That image format isn't supported. On iPhone, use a JPG or PNG (not HEIC).";
  }
  if (status >= 500 || !status) return "Slide upload failed. Please check your connection and try again.";
  return (typeof detail === "string" && detail) || (detail && detail.message) || e?.message || "Upload failed";
};

/**
 * Sequential slide upload queue. Preserves picked order; on a failure the
 * queue PAUSES on that slide (Retry / Skip) so order is kept and earlier
 * slides stay registered. Each item has a stable client_key so a retried
 * registration never duplicates; a finished Cloudinary upload is reused.
 */
export const useSlideUploadQueue = (roomId) => {
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const itemsRef = useRef([]);
  const runningRef = useRef(false);

  const patch = (key, p) => {
    itemsRef.current = itemsRef.current.map((it) => (it.key === key ? { ...it, ...p } : it));
    setItems(itemsRef.current);
  };

  const run = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setRunning(true);
    try {
      for (;;) {
        const it = itemsRef.current.find((x) => x.status === "pending");
        if (!it) break;
        patch(it.key, { status: "uploading", error: null });
        try {
          let url = it.image_url;
          if (!url) {
            const { file } = it;
            const form = new FormData();
            form.append("image", file);
            const up = await api.post("/posts/upload-image", form, { headers: { "Content-Type": "multipart/form-data" } });
            url = up.data?.image_url || up.data?.url;
            if (!url) throw new Error("No image_url returned");
            patch(it.key, { image_url: url });
          }
          await api.post(`/live/rooms/${roomId}/presentation/slide`, { image_url: url, client_key: it.key });
          patch(it.key, { status: "done" });
        } catch (e) {
          patch(it.key, { status: "failed", error: describeUploadError(e) });
          break;
        }
      }
    } finally {
      runningRef.current = false;
      setRunning(false);
    }
  }, [roomId]);

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    const rejected = [];
    const fresh = [];
    files.forEach((file) => {
      const err = validateSlideFile(file);
      if (err) rejected.push({ name: file.name, error: err });
      else fresh.push({ key: newKey(), file, name: file.name, status: "pending", image_url: null, error: null });
    });
    const keep = itemsRef.current.filter((x) => x.status !== "done" && x.status !== "skipped");
    itemsRef.current = [...keep, ...fresh];
    setItems(itemsRef.current);
    if (fresh.length) run();
    return { accepted: fresh.length, rejected };
  }, [run]);

  const retry = useCallback(() => {
    itemsRef.current = itemsRef.current.map((x) => (x.status === "failed" ? { ...x, status: "pending" } : x));
    setItems(itemsRef.current);
    run();
  }, [run]);

  const skip = useCallback(() => {
    itemsRef.current = itemsRef.current.map((x) => (x.status === "failed" ? { ...x, status: "skipped" } : x));
    setItems(itemsRef.current);
    run();
  }, [run]);

  const total = items.length;
  const doneCount = items.filter((x) => x.status === "done" || x.status === "skipped").length;
  const currentIdx = items.findIndex((x) => x.status === "uploading");
  const failed = items.find((x) => x.status === "failed") || null;
  return { items, running, total, doneCount, currentIdx, failed, addFiles, retry, skip };
};

export default useSlideUploadQueue;
